import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import {
  EditableComponent,
  RegistrationsComponent,
} from '@dpr-details/models/component';
import {
  DprDetailsGridBaseComponent,
  DprProgressWithoutTargetGridData,
} from '@dpr-details/models/grid';
import { NumericEditComponent, TextDisplayComponent } from '@grid/components';
import {
  GridAlignment,
  GridCellContentAlignment,
  GridSortDirection,
  GridSortMode,
} from '@grid/enums';
import { GridOptions, GridColumn } from '@grid/models';
import { DecimalDisplayComponent } from '@shared/components';
import { ProgressWithoutTargetTerminology } from '@shared/models/project-setup';
import { Constants } from '@shared/utils';
import { GridColumns, GridDefaultLabels } from '@shared/utils/grid';

@Component({
  selector: 'app-dpr-progress-without-target-grid',
  templateUrl: './dpr-progress-without-target-grid.component.html',
  styleUrls: ['./dpr-progress-without-target-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprProgressWithoutTargetGridComponent
  extends DprDetailsGridBaseComponent<DprProgressWithoutTargetGridData>
  implements
    RegistrationsComponent<DprProgressWithoutTargetGridData>,
    EditableComponent,
    OnChanges
{
  readonlyProgressWithoutTargetGridColumn = {
    header: {
      alignment: GridAlignment.Right,
    },
    columnAlignment: GridAlignment.Right,
    minWidth: 200,
    display: {
      type: TextDisplayComponent,
    },
    allowSorting: false,
    allowEditing: false,
    tabIndex: -1,
    disableFocusedBackground: true,
  };

  constructor() {
    super();
  }

  private get progressWithoutTargetTerminology(): ProgressWithoutTargetTerminology {
    return this.selectedSubproject.data.progressWithoutTarget
      .progressWithoutTargetTerminology;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.predefinedChanges?.currentValue) {
      this.gridComponent?.api?.setPredefinedChanges?.(this.predefinedChanges);
    }

    if (changes.selectedSubproject?.currentValue && this.labeling) {
      this.gridColumns$.next(this.getGridColumns());
    }

    if (changes.canEdit) {
      this.gridOptions$.next(this.getGridOptions());
      this.gridColumns$.next(this.getGridColumns());
    }
  }

  protected getGridOptions(): GridOptions<DprProgressWithoutTargetGridData> {
    return {
      sequenceConfig: {
        disabled: true,
      },
      labeling: GridDefaultLabels,
      addConfig: {
        allowAdding: false,
        keepAutogeneratedIds: true,
      },
      editConfig: {
        allowEditing: this.canEdit,
      },
      sortConfig: {
        mode: GridSortMode.Fixed,
        definition: [
          {
            columnDef: 'priority',
            direction: GridSortDirection.Ascending,
          },
        ],
      },
      saveConfig: {
        isExternalSave: true,
      },
      events: {
        onGridRegistered: this.onGridRegistered.bind(this),
      },
    };
  }
  protected getGridColumns(): GridColumn<DprProgressWithoutTargetGridData>[] {
    return [
      {
        columnDef: 'name',
        header: {
          name: this.progressWithoutTargetTerminology.block,
          alignment: GridAlignment.Left,
        },
        ...this.readonlyProgressWithoutTargetGridColumn,
        columnAlignment: GridAlignment.Left,
      },
      {
        columnDef: 'clearedToday',
        header: {
          name: this.progressWithoutTargetTerminology.clearedToday,
          alignment: GridAlignment.Right,
        },
        minWidth: 200,
        columnAlignment: GridAlignment.Right,
        display: {
          type: DecimalDisplayComponent,
        },
        edit: {
          type: NumericEditComponent,
          config: {
            alignment: GridCellContentAlignment.Right,
            options: {
              allowDecimals: true,
              decimalPrecision: 2,
              min: 0,
              max: Constants.NumericOptions.intMax,
              step: 0.01,
            },
          },
        },
        onCellEdited: (data: DprProgressWithoutTargetGridData) => {
          data.clearedTotal =
            (data.clearedTotalInitialValue || 0) + data.clearedToday;
        },
      },
      {
        columnDef: 'clearedTotal',
        ...this.readonlyProgressWithoutTargetGridColumn,
        header: {
          ...this.readonlyProgressWithoutTargetGridColumn.header,
          name: this.progressWithoutTargetTerminology.clearedTotal,
        },
        recomputeRowOnChange: true,
        display: {
          type: DecimalDisplayComponent,
          config: {
            alignment: GridCellContentAlignment.Right,
          },
        },
      },
      GridColumns.comment,
    ];
  }
}
